'use client';

import {CaptchaContextProvider} from '@shared/Captcha/Client/CaptchaContext';
import {HydrateCurrentService} from '@store/CurrentService/HydrateCurrentService';
import {retryPromise} from '@utils/retryPromise';
import {registerStoreForEnvVariablesAccess} from '@web-catalog/env';
import {createStore, Provider as JotaiProvider} from 'jotai';
import dynamic from 'next/dynamic';
import React, {FC, PropsWithChildren, useMemo, useRef} from 'react';

import {OmicronDTO} from '@web-catalog/api/omicronApi';

import {AppCategory} from '@web-catalog/types/category';
import {EnvConfig} from '@web-catalog/types/env';

import {HydrateAtoms} from '@web-catalog/store';

import {logTmrEvent} from '@web-catalog/utils/analytics';

import {MultiProviderFloorAd} from '../Ads/MultiProviderFloorAd';
import {YandexAdsContextProviderWrapper} from '../Ads/YandexAdsContextProviderWrapper';
import HeadlessUiDocumentPaddingObserver from './parts/HeadlessUiDocumentPaddingObserver';
import {OmicronClientInitializer} from './parts/OmicronClientInitializer';
import {PageViewReporter} from './parts/PageViewReporter';

const ExternalUrlModalController = dynamic(
  retryPromise(
    () =>
      import(
        /* webpackChunkName: "externalUrlModal" */ '@web-catalog/components/Linkify/ExternalUrlModalController'
      ),
    {fallback: {default: () => <></>}},
  ),
  {ssr: false},
);

const InpReporter = dynamic(
  async () =>
    import(/* webpackChunkName: "inp" */ './parts/InpReporter').catch(
      (error) => {
        console.error('Failed to load inp chunk', error);
        return () => null;
      },
    ),
  {ssr: false},
);

type Props = PropsWithChildren<{
  envConfig: EnvConfig;
  categories?: {
    content: Array<AppCategory>;
    contentGames: Array<AppCategory>;
  };
  omicron: OmicronDTO;
}>;

const useInitProvider = () => {
  const {current: store} = useRef(createStore());
  registerStoreForEnvVariablesAccess(store);

  return store;
};

export const Providers: FC<Props> = ({
  children,
  envConfig,
  categories,
  omicron,
}) => {
  const store = useInitProvider();

  const categoriesInit = useMemo(
    () => ({
      appsCategories: categories?.content ?? [],
      gamesCategories: categories?.contentGames ?? [],
    }),
    [categories],
  );

  return (
    <JotaiProvider store={store}>
      <HydrateAtoms
        envConfig={envConfig}
        categories={categoriesInit}
        omicron={omicron}
      >
        <HydrateCurrentService service="catalog" />

        <YandexAdsContextProviderWrapper>
          <HeadlessUiDocumentPaddingObserver />

          <OmicronClientInitializer omicron={omicron} />

          <CaptchaContextProvider
            allowBypassCaptcha={envConfig.ALLOW_BYPASS_CAPTCHA === 'true'}
            vkInitEndpoint="/catalog/api/vk-captcha-init"
            logAnalyticsEvent={logTmrEvent}
          >
            {children}
          </CaptchaContextProvider>

          <MultiProviderFloorAd />

          <PageViewReporter />
          <ExternalUrlModalController />
          {omicron.config.INP_REPORTING_THRESHOLD_MS > 0 && <InpReporter />}
        </YandexAdsContextProviderWrapper>
      </HydrateAtoms>
    </JotaiProvider>
  );
};
